import React, { useCallback, useMemo, useState } from 'react';
import type { CrudTableColumn } from '../crud/CrudTable';
import CrudTable, { useCrudState, useStandardCrudHandlers } from '../crud/CrudTable';
import type { AbortableRequest } from '@dr-pam/common-components/Utils/FetchUtils';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { UpgradePathDiscountListFragment, UpgradePromptListFragment } from '../../graphql/graphql';
import { useUpgradeService } from '../../services/UpgradeService';
import { ActionIcon, Badge, Flex, Group, Menu, Select, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconPencil, IconTrash } from '@tabler/icons-react';
import StatusBadge from '../content/StatusBadge';
import { AddUpgradePromptModal, EditUpgradePromptModal } from './UpgradePromptModal';

type StatusFilter = 'all' | 'active' | 'inactive';

export interface UpgradePromptsListProps {
	paths: UpgradePathDiscountListFragment[];
	isLoadingPaths: boolean;
}

// NOTE: Engagement analytics (views, clicks, conversions) are not yet implemented.
// Currently showing placeholder data (0) until backend engagement tracking is complete.
// This is planned for a future PR after the core upgrade system is deployed.
//
// This component displays all upgrade prompts (banners, modals, toasts) in a table format.
// It's the "what's showing to users right now?" dashboard for prompts, allowing marketing teams
// to see which prompts are active, when they display, who they target, and how many users engaged.
// Includes filtering by status and upgrade path to help teams manage multiple campaigns.
export default function UpgradePromptsList({ paths, isLoadingPaths }: UpgradePromptsListProps) {
	const upgradeService = useUpgradeService();
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const [pathFilter, setPathFilter] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deletingPromptId, setDeletingPromptId] = useState<string | null>(null);

	const fetchPrompts = useCallback((): AbortableRequest<UpgradePromptListFragment[]> | null => {
		const request = upgradeService.getAllPrompts();
		return {
			...request,
			response: request.response.catch((error) => {
				NotificationUtils.showError(error as Error, 'Failed to load upgrade prompts');
				return [];
			}),
		};
	}, [upgradeService]);

	const crudState = useCrudState<UpgradePromptListFragment, UpgradePromptListFragment>(fetchPrompts);

	const crudHandlers = useStandardCrudHandlers<UpgradePromptListFragment, UpgradePromptListFragment>({
		entityName: 'upgrade prompt',
		crudState,
		addModalFactory: (props) => <AddUpgradePromptModal {...props} paths={paths} />,
		editModalFactory: (props) => <EditUpgradePromptModal {...props} paths={paths} />,
		delete: async (prompt) => {
			setIsDeleting(true);
			setDeletingPromptId(prompt.id);
			try {
				await upgradeService.deletePrompt(prompt.id);
				crudState.refetch();
				NotificationUtils.showSuccess('Upgrade prompt deleted', prompt.name);
			} catch (error) {
				NotificationUtils.showError(error as Error, 'Failed to delete upgrade prompt');
				// Don't refetch - the delete failed
			} finally {
				setIsDeleting(false);
				setDeletingPromptId(null);
			}
		},
		nameAccessor: (prompt) => prompt.name,
	});

	const filteredItems = useMemo(() => {
		if (!crudState.items) {
			return undefined;
		}

		return crudState.items.filter((prompt) => {
			if (statusFilter !== 'all') {
				const matchesStatus = statusFilter === 'active' ? prompt.isActive : !prompt.isActive;
				if (!matchesStatus) {
					return false;
				}
			}
			if (pathFilter) {
				return prompt.upgradePathDiscountId === pathFilter;
			}
			return true;
		});
	}, [crudState.items, pathFilter, statusFilter]);

	const pathOptions = useMemo(() => paths.map((path) => ({ value: path.id, label: path.name })), [paths]);

	return (
		<Stack gap="lg">
			<Group gap="lg" align="flex-end">
				<div>
					<Text fw={600} mb={4}>
						Status
					</Text>
					<SegmentedControl
						value={statusFilter}
						onChange={(value) => setStatusFilter(value as StatusFilter)}
						data={[
							{ label: 'All', value: 'all' },
							{ label: 'Active', value: 'active' },
							{ label: 'Inactive', value: 'inactive' },
						]}
					/>
				</div>
				<Select
					label="Filter by upgrade path"
					placeholder="All paths"
					data={pathOptions}
					clearable
					value={pathFilter}
					onChange={setPathFilter}
					style={{ maxWidth: 260 }}
					disabled={isLoadingPaths}
				/>
			</Group>
			<CrudTable
				entityName="upgrade prompt"
				headers={crudHeaders}
				columns={(prompt) => buildColumns(prompt, crudHandlers, isDeleting, deletingPromptId)}
				items={filteredItems}
				isLoading={crudState.isFetching}
				onAddClicked={crudHandlers.addHandler}
			/>
		</Stack>
	);
}

const crudHeaders: CrudTableColumn[] = [
	{ children: 'Name' },
	{ children: 'Upgrade Path' },
	{ children: 'Show From Day' },
	{ children: 'Show Until Day' },
	{ children: 'Type' },
	{ children: 'Frequency' },
	{ children: 'Views' },
	{ children: 'Clicks' },
	{ children: 'Conversions' },
	{ children: 'Conversion Rate' },
	{ children: 'Active' },
	{ children: '' },
];

type PromptCrudHandlers = ReturnType<
	typeof useStandardCrudHandlers<UpgradePromptListFragment, UpgradePromptListFragment>
>;

function buildColumns(
	prompt: UpgradePromptListFragment,
	handlers: PromptCrudHandlers,
	isDeleting: boolean,
	deletingPromptId: string | null,
): CrudTableColumn[] {
	// Subscription type targeting is now handled at the upgrade path level via fromProductSubscriptionTypeId.
	// The upgrade path's fromProductSubscriptionTypeId directly references the subscription type.
	const totalEngagements = prompt._count?.engagements ?? 0;

	return [
		{
			children: (
				<Stack gap={2}>
					<Text fw={600}>{prompt.name}</Text>
					{prompt.title && (
						<Text size="sm" c="dimmed">
							{prompt.title}
						</Text>
					)}
				</Stack>
			),
		},
		{
			children: (
				<Text size="sm" fw={500}>
					{prompt.upgradePathDiscount?.name ?? '—'}
				</Text>
			),
		},
		{
			children: (
				<Text size="sm" ta="center">
					{prompt.showFromDayAfterPurchase}
				</Text>
			),
		},
		{
			children: (
				<Text size="sm" ta="center">
					{prompt.showUntilDayAfterPurchase ?? '∞'}
				</Text>
			),
		},
		{
			children: (
				<Badge variant="light" color="blue">
					{prompt.promptType}
				</Badge>
			),
		},
		{
			children: (
				<Badge variant="light" color="grape">
					{prompt.frequency}
				</Badge>
			),
		},
		{
			children: (
				<Text size="sm" ta="center">
					{totalEngagements}
				</Text>
			),
		},
		{
			children: (
				<Tooltip label="Coming soon">
					<Text size="sm" ta="center" c="dimmed">
						--
					</Text>
				</Tooltip>
			),
		},
		{
			children: (
				<Tooltip label="Coming soon">
					<Text size="sm" ta="center" c="dimmed">
						--
					</Text>
				</Tooltip>
			),
		},
		{
			children: (
				<Tooltip label="Coming soon">
					<Text size="sm" ta="center" c="dimmed">
						--
					</Text>
				</Tooltip>
			),
		},
		{
			children: <StatusBadge status={prompt.isActive ? 'published' : 'draft'} />,
		},
		{
			children: (
				<Flex justify="flex-end">
					<Menu shadow="md" width={160}>
						<Menu.Target>
							<ActionIcon variant="subtle" color="dark">
								<IconDotsVertical size={18} />
							</ActionIcon>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item
								leftSection={<IconPencil size={16} />}
								onClick={() => handlers.editHandler(prompt)}
								disabled={isDeleting && deletingPromptId === prompt.id}
							>
								Edit
							</Menu.Item>
							<Menu.Item
								leftSection={<IconTrash size={16} />}
								color="red"
								onClick={() => handlers.deleteHandler(prompt)}
								disabled={isDeleting && deletingPromptId === prompt.id}
							>
								{isDeleting && deletingPromptId === prompt.id ? 'Deleting...' : 'Delete'}
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</Flex>
			),
		},
	];
}
